import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { autoPostAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import {
  X, Wand2, Loader2, Sparkles, Check, ArrowLeft, Lightbulb,
  Zap, Pencil, Plus,
} from 'lucide-react';

/**
 * Writing-style analyzer modal.
 *
 * Flow (3 steps):
 *   Step 1 – "import": user can either click "Aus Social-Profil
 *            importieren" (one-click pull from their connected
 *            Instagram/TikTok/etc. via Upload Post /media endpoint)
 *            OR paste posts manually into the textarea. We then run
 *            the analyzer and skip straight to review.
 *   Step 2 – "review": we display the AI-generated summary plus
 *            structured fields. EVERY field is now EDITABLE. User
 *            can tweak the AI's read (correct a misdetected tone,
 *            add/remove signature phrases, etc.) before accepting.
 *   Step 3 – "accept": clicking "Übernehmen" auto-saves via
 *            CaptionStyleSection's onAutoSave (no manual Save click
 *            needed).
 *
 * The "voice cloning" framing of earlier copy was confusing. We
 * call this "Schreibstil" / "writing style" now since that's what
 * we're actually doing (analyzing word choice, tone, structure).
 */
export default function BrandVoiceAnalyzer({ onClose, onAccept, currentProfile }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('import');   // 'import' | 'review'
  const [text, setText] = useState('');
  const [language, setLanguage] = useState('de');
  const [analyzing, setAnalyzing] = useState(false);
  const [autoImporting, setAutoImporting] = useState(false);
  const [autoImportInfo, setAutoImportInfo] = useState(null); // {captions_used, pulled_per_platform}
  // Profile is a working copy so the review fields can be edited
  // directly. We snapshot the AI-returned profile here on analysis
  // success; user edits go into this state until they Accept.
  const [profile, setProfile] = useState(currentProfile || null);

  // Close on Escape. Standard modal behavior across the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Split the textarea into individual captions. We accept any of:
  //   - blank lines between posts (most natural)
  //   - explicit `---` separators
  //   - "Post 1:", "Post 2:" style numbering (regex stripped)
  // ...so the user can paste however feels natural.
  const parseCaptions = (raw) => {
    if (!raw) return [];
    const stripped = raw.replace(/^[Pp]ost\s*#?\s*\d+\s*[:.\-]\s*/gm, '');
    const chunks = stripped.split(/\n\s*\n|---+/g);
    return chunks.map((c) => c.trim()).filter((c) => c.length > 15);
  };
  const captions = parseCaptions(text);
  const canAnalyze = captions.length >= 2;

  const runAnalyze = async () => {
    if (!canAnalyze) return;
    setAnalyzing(true);
    try {
      const res = await autoPostAPI.analyzeBrandVoice(captions, language);
      const p = res.data?.profile || {};
      if (!p.tone && !p.summary) {
        toast.error(t('autoPost.brandVoice.notEnough'));
        setAnalyzing(false);
        return;
      }
      setProfile(p);
      setStep('review');
    } catch (e) {
      toast.error(t('autoPost.brandVoice.analyzeFailed'));
    } finally {
      setAnalyzing(false);
    }
  };

  const runAutoImport = async () => {
    setAutoImporting(true);
    setAutoImportInfo(null);
    try {
      const res = await autoPostAPI.autoImportBrandVoice();
      const data = res.data || {};
      const p = data.profile;
      // Empty profile → not enough posts. The backend returns
      // { profile: null, captions_used: [], warning: 'not_enough_posts' }
      if (!p || (!p.tone && !p.summary)) {
        if (data.warning === 'not_enough_posts') {
          toast.error(t('autoPost.brandVoice.autoImportNotEnough'));
        } else {
          toast.error(t('autoPost.brandVoice.autoImportFailed'));
        }
        return;
      }
      // Success. Show how many captions came from which platforms
      // so the user understands what the AI looked at.
      setAutoImportInfo({
        captions_used: data.captions_used || [],
        pulled_per_platform: data.pulled_per_platform || {},
      });
      const platformList = Object.entries(data.pulled_per_platform || {})
        .map(([p, n]) => `${p} (${n})`).join(', ');
      toast.success(t('autoPost.brandVoice.autoImportSuccess', {
        count: (data.captions_used || []).length,
        platforms: platformList,
      }));
      setProfile(p);
      // Also drop the captions into the textarea so the user can
      // see them, edit them, or re-run the analysis on a subset.
      setText((data.captions_used || []).join('\n\n---\n\n'));
      setStep('review');
    } catch (e) {
      const errBody = e?.response?.data?.detail;
      if (errBody && typeof errBody === 'object' && errBody.error === 'no_social_connected') {
        toast.error(errBody.message || t('autoPost.brandVoice.autoImportNoSocial'));
      } else {
        toast.error(t('autoPost.brandVoice.autoImportFailed'));
      }
    } finally {
      setAutoImporting(false);
    }
  };

  const accept = () => {
    if (!profile) return;
    onAccept?.(profile);
    toast.success(t('autoPost.brandVoice.saved'));
  };

  // Field edit helpers. Keep the profile object internally consistent.
  const setField = (key, value) => setProfile((p) => ({ ...(p || {}), [key]: value }));
  const setListField = (key, value) => setProfile((p) => ({ ...(p || {}), [key]: value }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="glass border-2 border-[#1f4d3a]/30 w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#fffdf7]/95 backdrop-blur-md border-b border-[#e3dccb] px-5 sm:px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-[#23211a] flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#1f4d3a]" />
            {t('autoPost.brandVoice.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a] transition"
            aria-label={t('common.close') || 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'import' && (
          <div className="px-5 sm:px-6 py-5 space-y-4">
            {/* Re-analyze context. Show current style if exists. */}
            {currentProfile?.summary && (
              <div className="p-3 rounded-lg bg-[#c7a869]/5 border border-[#c7a869] flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-[#9a6d1f] mt-0.5 shrink-0" />
                <div className="text-xs text-[#7a5619]/90 leading-relaxed">
                  <p className="font-medium text-[#9a6d1f] mb-0.5">
                    {t('autoPost.brandVoice.currentlyLearned')}
                  </p>
                  <p>{currentProfile.summary || currentProfile.tone}</p>
                  <p className="text-[11px] text-[#9a6d1f]/80 mt-1.5">
                    {t('autoPost.brandVoice.replaceHint')}
                  </p>
                </div>
              </div>
            )}

            {/* First-run why card. Hidden during re-analyze. */}
            {!currentProfile?.summary && (
              <div className="p-3 rounded-lg bg-[#f0f3ec] border border-[#1f4d3a]/20 flex items-start gap-2">
                <Lightbulb className="w-4 h-4 text-[#1f4d3a] mt-0.5 shrink-0" />
                <p className="text-xs text-[#23211a]/90 leading-relaxed">
                  {t('autoPost.brandVoice.why')}
                </p>
              </div>
            )}

            {/* Auto-import. Primary path. One click, no manual paste.
                Pulls from connected social profile via Upload Post. */}
            <Button
              type="button"
              onClick={runAutoImport}
              disabled={autoImporting || analyzing}
              className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white py-5 text-base"
              data-testid="brand-voice-auto-import-btn"
            >
              {autoImporting ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('autoPost.brandVoice.autoImportRunning')}</>
              ) : (
                <><Zap className="w-5 h-5 mr-2" />
                  {t('autoPost.brandVoice.autoImportBtn')}</>
              )}
            </Button>

            {/* Divider. Fallback to manual paste. */}
            <div className="flex items-center gap-3 pt-2">
              <div className="flex-1 h-px bg-[#e9e2d2]" />
              <span className="text-xs text-[#8a8474] uppercase tracking-wider">
                {t('common.or') || 'oder'}
              </span>
              <div className="flex-1 h-px bg-[#e9e2d2]" />
            </div>

            {/* Manual paste fallback. */}
            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('autoPost.brandVoice.orPasteManually')}
              </label>
              <p className="text-[11px] text-[#8a8474] mb-2">
                {t('autoPost.brandVoice.captionsHint')}
              </p>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={8}
                placeholder={t('autoPost.brandVoice.captionsPlaceholder')}
                className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a] focus:ring-2 focus:ring-[#1f4d3a]/20 resize-y font-mono leading-relaxed"
                data-testid="brand-voice-textarea"
              />
              <div className="flex items-center justify-between text-[11px] mt-1.5">
                <span className={captions.length >= 2 ? 'text-[#1e7a4f]' : 'text-[#8a8474]'}>
                  {captions.length === 0
                    ? t('autoPost.brandVoice.zero')
                    : t('autoPost.brandVoice.detected', { count: captions.length })}
                </span>
                {!canAnalyze && captions.length === 1 && (
                  <span className="text-[#9a6d1f]">
                    {t('autoPost.brandVoice.needMore')}
                  </span>
                )}
              </div>
            </div>

            {/* Language toggle */}
            <div className="flex items-center gap-3 text-xs">
              <span className="text-[#6f6a5c]">
                {t('autoPost.brandVoice.languageLabel')}
              </span>
              <button
                type="button"
                onClick={() => setLanguage('de')}
                className={`px-2.5 py-1 rounded-full border ${
                  language === 'de'
                    ? 'bg-[#dfe8db] text-[#23211a] border-[#1f4d3a]/50'
                    : 'bg-[#fffdf7] text-[#6f6a5c] border-[#d8d0bd]'
                }`}
              >DE</button>
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`px-2.5 py-1 rounded-full border ${
                  language === 'en'
                    ? 'bg-[#dfe8db] text-[#23211a] border-[#1f4d3a]/50'
                    : 'bg-[#fffdf7] text-[#6f6a5c] border-[#d8d0bd]'
                }`}
              >EN</button>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-[#d8d0bd] text-[#4a463b] sm:flex-1"
              >
                {t('common.cancel') || 'Abbrechen'}
              </Button>
              <Button
                type="button"
                onClick={runAnalyze}
                disabled={!canAnalyze || analyzing || autoImporting}
                className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white sm:flex-1"
                data-testid="brand-voice-analyze-btn"
              >
                {analyzing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('autoPost.brandVoice.analyzing')}</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />
                    {t('autoPost.brandVoice.analyze')}</>
                )}
              </Button>
            </div>
          </div>
        )}

        {step === 'review' && profile && (
          <div className="px-5 sm:px-6 py-5 space-y-4">
            {/* Source info when auto-imported */}
            {autoImportInfo?.pulled_per_platform &&
             Object.keys(autoImportInfo.pulled_per_platform).length > 0 && (
              <div className="p-2.5 rounded-md bg-[#f0f3ec] border border-[#1f4d3a]/20 text-[11px] text-[#2e6b50]/90">
                {t('autoPost.brandVoice.autoImportSuccess', {
                  count: autoImportInfo.captions_used.length,
                  platforms: Object.entries(autoImportInfo.pulled_per_platform)
                    .map(([p, n]) => `${p} (${n})`).join(', '),
                })}
              </div>
            )}

            {/* Top summary card. Editable */}
            <div className="p-3 rounded-lg bg-[#1e7a4f]/5 border border-[#1e7a4f]/20">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-[#1e7a4f] shrink-0" />
                <p className="text-xs font-medium text-[#1e7a4f]">
                  {t('autoPost.brandVoice.gotIt')}
                </p>
                <span className="text-[10px] text-[#1e7a4f]/70 ml-auto inline-flex items-center gap-1">
                  <Pencil className="w-3 h-3" />
                  {t('autoPost.brandVoice.editHint')}
                </span>
              </div>
              <textarea
                value={profile.summary || profile.tone || ''}
                onChange={(e) => setField('summary', e.target.value)}
                rows={2}
                maxLength={600}
                className="w-full bg-[#f7f3ea]/30 border border-[#1e7a4f]/20 rounded-md px-2.5 py-1.5 text-xs text-[#155c3b] leading-relaxed focus:outline-none focus:border-[#1e7a4f]/50 resize-none"
              />
            </div>

            {/* Tone */}
            <EditableField
              label={t('autoPost.brandVoice.tone')}
              value={profile.tone || ''}
              onChange={(v) => setField('tone', v)}
              maxLength={300}
            />
            <EditableField
              label={t('autoPost.brandVoice.hookStyle')}
              value={profile.hook_style || ''}
              onChange={(v) => setField('hook_style', v)}
              maxLength={200}
            />
            <EditableField
              label={t('autoPost.brandVoice.ctaStyle')}
              value={profile.cta_style || ''}
              onChange={(v) => setField('cta_style', v)}
              maxLength={200}
            />
            <EditableField
              label={t('autoPost.brandVoice.structure')}
              value={profile.structure || ''}
              onChange={(v) => setField('structure', v)}
              maxLength={300}
            />

            {/* Signature phrases. Chip editor */}
            <EditableChipList
              label={t('autoPost.brandVoice.signaturePhrases')}
              value={profile.signature_phrases || []}
              onChange={(v) => setListField('signature_phrases', v)}
              placeholder={t('autoPost.brandVoice.addPhrase')}
              maxItems={8}
              chipTone="violet"
            />

            {/* Avoid. Chip editor */}
            <EditableChipList
              label={t('autoPost.brandVoice.avoid')}
              value={profile.avoid || []}
              onChange={(v) => setListField('avoid', v)}
              placeholder={t('autoPost.brandVoice.addAvoid')}
              maxItems={8}
              chipTone="red"
            />

            {/* Emoji density. Preset chips */}
            <div>
              <p className="text-xs text-[#6f6a5c] mb-1.5">
                {t('autoPost.brandVoice.emojiDensity')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['none', 'sparing', 'moderate', 'generous'].map((val) => {
                  const active = (profile.emoji_density || 'moderate') === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setField('emoji_density', val)}
                      className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                        active
                          ? 'bg-[#dfe8db] text-[#23211a] border-[#1f4d3a]/50'
                          : 'bg-[#f2ecdf] text-[#6f6a5c] border-[#d8d0bd] hover:border-[#1f4d3a]/50'
                      }`}
                    >
                      {t(`autoPost.captionStyle.emoji.${val}`) || val}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('import')}
                className="border-[#d8d0bd] text-[#4a463b] sm:flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                {t('autoPost.brandVoice.retry')}
              </Button>
              <Button
                type="button"
                onClick={accept}
                className="bg-[#1e7a4f] hover:bg-[#1e7a4f] text-white sm:flex-1"
                data-testid="brand-voice-accept-btn"
              >
                <Check className="w-4 h-4 mr-1.5" />
                {t('autoPost.brandVoice.accept')}
              </Button>
            </div>
            <p className="text-[11px] text-[#8a8474] text-center">
              {t('autoPost.brandVoice.acceptHint')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


// ── Editable single-line field ────────────────────────────────────────
// Inline editable text input. Renders as a label + Input. Empty value
// hides the label until the user types or focuses (cleaner visual when
// AI didn't detect that aspect).
function EditableField({ label, value, onChange, maxLength = 300 }) {
  return (
    <div>
      <label className="text-xs text-[#6f6a5c] mb-1 block">{label}</label>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxLength}
        className="bg-[#f7f3ea]/30 border-[#e3dccb] text-sm text-[#23211a] focus:border-[#1f4d3a]"
      />
    </div>
  );
}


// ── Editable chip list ────────────────────────────────────────────────
// For signature_phrases + avoid. Each entry is a chip; click X to
// remove; type into the inline input to add. Auto-trims, ignores
// duplicates, caps at maxItems.
function EditableChipList({ label, value, onChange, placeholder, maxItems = 8, chipTone = 'violet' }) {
  const inputRef = useRef(null);
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) { setDraft(''); return; }
    if (value.length >= maxItems) { setDraft(''); return; }
    onChange([...value, v]);
    setDraft('');
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const chipClass = chipTone === 'red'
    ? 'bg-[#f7e8e3] text-[#8c3a29] border-[#b3543f]/40'
    : 'bg-[#e9efe7] text-[#2e6b50] border-[#1f4d3a]/30';

  return (
    <div>
      <label className="text-xs text-[#6f6a5c] mb-1 block">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {value.map((v, i) => (
          <span key={`${v}-${i}`} className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border ${chipClass}`}>
            „{v}"
            <button
              type="button"
              onClick={() => remove(i)}
              className="hover:text-[#23211a]"
              aria-label="remove"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          disabled={value.length >= maxItems}
          className="bg-[#f7f3ea]/30 border-[#e3dccb] text-xs text-[#23211a] focus:border-[#1f4d3a] flex-1"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={add}
          disabled={!draft.trim() || value.length >= maxItems}
          className="border-[#d8d0bd] text-[#23211a] shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
